import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useSync } from '../contexts/SyncContext';
import { getFlocs } from '../services/flocService';
import { getAssets } from '../services/assetService';
import { getInspections } from '../services/inspectionService';
import { getActiveSchedules } from '../services/scheduleService';
import {
  MapPinIcon,
  CubeIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

export function DashboardPage() {
  const { currentOrg } = useOrg();
  const { isOnline, isSyncing, syncData, lastSyncTime, getStorageInfo } = useSync();

  const [stats, setStats] = useState({
    flocs: 0,
    assets: 0,
    inspections: 0,
  });
  const [inspectionStats, setInspectionStats] = useState({
    completed: 0,
    inProgress: 0,
    scheduled: 0,
    failed: 0,
  });
  const [recentInspections, setRecentInspections] = useState<any[]>([]);
  const [upcomingInspections, setUpcomingInspections] = useState<any[]>([]);
  const [activeSchedules, setActiveSchedules] = useState<any[]>([]);
  const [storageInfo, setStorageInfo] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    loadStats();
    loadStorageInfo();
  }, [currentOrg]);

  const loadStats = async () => {
    if (!currentOrg) {
      setLoadingStats(false);
      return;
    }

    setLoadingStats(true);
    try {
      // Helper function to convert various timestamp formats to milliseconds
      const getTime = (d: any) => {
        if (!d) return 0;
        if (d instanceof Date) return d.getTime();
        if (typeof d === 'object' && 'toDate' in d) return d.toDate().getTime();
        return new Date(d).getTime();
      };

      const [flocs, assets, inspections, schedules] = await Promise.all([
        getFlocs(currentOrg.id),
        getAssets(currentOrg.id),
        getInspections(currentOrg.id),
        getActiveSchedules(currentOrg.id),
      ]);

      // Create lookup maps for FLOCs and Assets
      const flocMap = new Map(flocs.map(f => [f.id, f]));
      const assetMap = new Map(assets.map(a => [a.id, a]));

      setStats({
        flocs: flocs.length,
        assets: assets.length,
        inspections: inspections.length,
      });

      // Calculate inspection status breakdown
      const statusCounts = {
        completed: inspections.filter(i => i.status === 'completed').length,
        inProgress: inspections.filter(i => i.status === 'in_progress').length,
        scheduled: inspections.filter(i => i.status === 'scheduled').length,
        failed: inspections.filter(i => i.status === 'failed').length,
      };
      setInspectionStats(statusCounts);

      // Enrich inspections with FLOC/Asset names
      const enrichedInspections = inspections.map(inspection => {
        let flocName = '';
        let flocCode = '';
        let assetName = '';
        let assetTag = '';

        // Get FLOC information
        if (inspection.flocId) {
          const floc = flocMap.get(inspection.flocId);
          if (floc) {
            flocName = floc.name;
            flocCode = floc.code || '';
          }
        }

        // Get Asset information
        if (inspection.assetId) {
          const asset = assetMap.get(inspection.assetId);
          if (asset) {
            assetName = asset.name || asset.serialNumber;
            assetTag = asset.assetId || ''; // assetId is the user-defined tag/code

            // If asset doesn't have a FLOC in inspection, use asset's current FLOC
            if (!flocName && asset.currentFlocId) {
              const floc = flocMap.get(asset.currentFlocId);
              if (floc) {
                flocName = floc.name;
                flocCode = floc.code || '';
              }
            }
          }
        }

        return {
          ...inspection,
          displayFlocName: flocName,
          displayFlocCode: flocCode,
          displayAssetName: assetName,
          displayAssetTag: assetTag,
        };
      });

      // Get recent inspections (last 5 completed or in-progress)
      const recentFiltered = enrichedInspections.filter(i =>
        i.status === 'completed' ||
        i.status === 'in_progress' ||
        i.status === 'pending_review' ||
        i.status === 'approved' ||
        i.status === 'rejected'
      );
      const sorted = [...recentFiltered].sort((a, b) => {
        const dateA = a.completedAt || a.startedAt || a.createdAt;
        const dateB = b.completedAt || b.startedAt || b.createdAt;
        return getTime(dateB) - getTime(dateA);
      });
      setRecentInspections(sorted.slice(0, 5));

      // Get upcoming scheduled inspections (next 7 days)
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcoming = enrichedInspections.filter(i => {
        if (i.status !== 'scheduled' || !i.scheduledDate) return false;
        const schedDate = getTime(i.scheduledDate);
        return schedDate >= now.getTime() && schedDate <= nextWeek.getTime();
      }).sort((a, b) => getTime(a.scheduledDate) - getTime(b.scheduledDate)).slice(0, 5);
      setUpcomingInspections(upcoming);

      // Get active schedules (top 5)
      setActiveSchedules(schedules.slice(0, 5));
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const info = await getStorageInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error('Failed to load storage info:', error);
    }
  };

  const handleSync = async () => {
    try {
      await syncData();
      await loadStorageInfo();
    } catch (error) {
      console.error('Sync failed:', error);
      alert('Failed to sync data');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        {isOnline && (
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="btn btn-secondary flex items-center"
          >
            <ArrowPathIcon className={`w-5 h-5 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Data'}
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total FLOCs</h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {loadingStats ? '-' : stats.flocs}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <MapPinIcon className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Assets</h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {loadingStats ? '-' : stats.assets}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <CubeIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Inspections</h3>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {loadingStats ? '-' : stats.inspections}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <ClipboardDocumentCheckIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Offline Storage Info */}
      {storageInfo && (
        <div className="card mb-8">
          <div className="flex items-start">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4 flex-shrink-0">
              <CloudArrowDownIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Offline Storage
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Your data is synced locally for offline access
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Storage Used:</span>
                  <span className="font-medium text-gray-900">
                    {storageInfo.usageMB.toFixed(2)} MB of {storageInfo.quotaMB.toFixed(0)} MB
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${Math.min(storageInfo.usagePercent, 100)}%` }}
                  />
                </div>
                {lastSyncTime && (
                  <p className="text-xs text-gray-500">
                    Last synced: {lastSyncTime.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Status Breakdown */}
      {stats.inspections > 0 && (
        <div className="card mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Inspection Analytics
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Chart */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Status Distribution</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Completed', value: inspectionStats.completed, color: '#10b981' },
                      { name: 'In Progress', value: inspectionStats.inProgress, color: '#f59e0b' },
                      { name: 'Scheduled', value: inspectionStats.scheduled, color: '#3b82f6' },
                      { name: 'Failed', value: inspectionStats.failed, color: '#ef4444' },
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {[
                      { name: 'Completed', value: inspectionStats.completed, color: '#10b981' },
                      { name: 'In Progress', value: inspectionStats.inProgress, color: '#f59e0b' },
                      { name: 'Scheduled', value: inspectionStats.scheduled, color: '#3b82f6' },
                      { name: 'Failed', value: inspectionStats.failed, color: '#ef4444' },
                    ].filter(item => item.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Status Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{inspectionStats.completed}</p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="w-8 h-8 text-yellow-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{inspectionStats.inProgress}</p>
                  <p className="text-sm text-gray-600">In Progress</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{inspectionStats.scheduled}</p>
                  <p className="text-sm text-gray-600">Scheduled</p>
                </div>
              </div>
              <div className="flex items-center">
                <XCircleIcon className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{inspectionStats.failed}</p>
                  <p className="text-sm text-gray-600">Failed</p>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="col-span-2 mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Completion Rate</span>
                  <span className="text-lg font-bold text-gray-900">
                    {stats.inspections > 0
                      ? Math.round((inspectionStats.completed / stats.inspections) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${stats.inspections > 0
                        ? (inspectionStats.completed / stats.inspections) * 100
                        : 0}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Inspections */}
      {recentInspections.length > 0 && (
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Inspections</h3>
            <Link to="/inspections" className="text-sm text-primary-600 hover:text-primary-700">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {recentInspections.map((inspection) => (
              <Link
                key={inspection.id}
                to={`/inspections/${inspection.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900 mb-1">{inspection.templateName}</p>
                  <div className="flex gap-3 text-sm text-gray-600">
                    {inspection.displayFlocName && (
                      <div>
                        <span className="font-medium">FLOC:</span> {inspection.displayFlocName}
                        {inspection.displayFlocCode && <span className="text-gray-500"> ({inspection.displayFlocCode})</span>}
                      </div>
                    )}
                    {inspection.displayAssetName && (
                      <div>
                        <span className="font-medium">Asset:</span> {inspection.displayAssetName}
                        {inspection.displayAssetTag && <span className="text-gray-500"> ({inspection.displayAssetTag})</span>}
                      </div>
                    )}
                    {!inspection.displayFlocName && !inspection.displayAssetName && (
                      <div>
                        <span className="font-medium">Site:</span> {inspection.siteName}
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    inspection.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : inspection.status === 'in_progress'
                      ? 'bg-yellow-100 text-yellow-800'
                      : inspection.status === 'scheduled'
                      ? 'bg-blue-100 text-blue-800'
                      : inspection.status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {inspection.status.replace('_', ' ')}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Two Column Layout for Upcoming Inspections and Active Schedules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Upcoming Inspections */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming This Week</h3>
            <Link to="/inspections" className="text-sm text-primary-600 hover:text-primary-700">
              View All
            </Link>
          </div>
          {upcomingInspections.length > 0 ? (
            <div className="space-y-3">
              {upcomingInspections.map((inspection) => {
                const schedDate = inspection.scheduledDate
                  ? (typeof inspection.scheduledDate === 'number'
                      ? new Date(inspection.scheduledDate)
                      : (inspection.scheduledDate as any).toDate())
                  : null;
                const daysUntil = schedDate
                  ? Math.ceil((schedDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                  : 0;

                return (
                  <Link
                    key={inspection.id}
                    to={`/inspections/${inspection.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1">{inspection.templateName}</p>
                      <div className="flex gap-3 text-sm text-gray-600">
                        {inspection.displayFlocName && (
                          <div>
                            <span className="font-medium">FLOC:</span> {inspection.displayFlocName}
                            {inspection.displayFlocCode && <span className="text-gray-500"> ({inspection.displayFlocCode})</span>}
                          </div>
                        )}
                        {inspection.displayAssetName && (
                          <div>
                            <span className="font-medium">Asset:</span> {inspection.displayAssetName}
                            {inspection.displayAssetTag && <span className="text-gray-500"> ({inspection.displayAssetTag})</span>}
                          </div>
                        )}
                        {!inspection.displayFlocName && !inspection.displayAssetName && (
                          <div>
                            <span className="font-medium">Site:</span> {inspection.siteName}
                          </div>
                        )}
                      </div>
                      {inspection.reservedBy && (
                        <div className="text-xs text-amber-700 mt-1">
                          Reserved by: {inspection.reservedByName}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {schedDate?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-xs text-gray-500">
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `in ${daysUntil} days`}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">No inspections scheduled for the next 7 days</p>
            </div>
          )}
        </div>

        {/* Active Schedules */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Schedules</h3>
            <Link to="/schedules" className="text-sm text-primary-600 hover:text-primary-700">
              View All
            </Link>
          </div>
          {activeSchedules.length > 0 ? (
            <div className="space-y-3">
              {activeSchedules.map((schedule: any) => {
                const nextDate = schedule.nextInspectionDate
                  ? (typeof schedule.nextInspectionDate === 'number'
                      ? new Date(schedule.nextInspectionDate)
                      : (schedule.nextInspectionDate as any).toDate())
                  : null;

                const formatRecurrence = () => {
                  const { frequency, interval } = schedule.recurrence;
                  const intervalText = interval > 1 ? `Every ${interval} ` : '';
                  switch (frequency) {
                    case 'daily': return `${intervalText}Day${interval > 1 ? 's' : ''}`;
                    case 'weekly': return `${intervalText}Week${interval > 1 ? 's' : ''}`;
                    case 'monthly': return `${intervalText}Month${interval > 1 ? 's' : ''}`;
                    case 'yearly': return `${intervalText}Year${interval > 1 ? 's' : ''}`;
                    default: return `Every ${interval} days`;
                  }
                };

                return (
                  <Link
                    key={schedule.id}
                    to="/schedules"
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{schedule.name}</p>
                      <p className="text-sm text-gray-600">{formatRecurrence()}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-xs text-gray-500">Next:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {nextDate?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-3">No active schedules yet</p>
              <Link to="/schedules" className="btn btn-primary btn-sm">
                Create Schedule
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Welcome Card */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Welcome to EasyDone-Pro
        </h2>
        <div className="space-y-3 text-gray-600">
          <p>
            {currentOrg?.name} is ready for asset inspections and data capture.
          </p>
          {stats.flocs === 0 && (
            <p className="text-primary-600">
              Get started by creating your first FLOC from the FLOCs page.
            </p>
          )}
          {stats.flocs > 0 && stats.assets === 0 && (
            <p className="text-primary-600">
              Great! Now add assets to your FLOCs from the Assets page.
            </p>
          )}
          {!isOnline && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <p className="text-yellow-800 font-medium">
                You're currently offline
              </p>
              <p className="text-yellow-700 text-sm mt-1">
                You can still view and edit data. Changes will sync when you're back online.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
